// src/features/patient/care-treatment/components/VisitDiagnoses.tsx

/**
 * Purpose : "Diagnoses from this visit" on a visit page: one row per
 * diagnosis (its name, its code in grey, Active / Inactive / Resolved), the
 * same name once, the first 5 then "Show all N"; a row opens that diagnosis
 * on the Diagnosis page, selected and highlighted (?focus=, as the header
 * search does). Only diagnoses the record itself links to the visit reach it
 * (care_timeline/timeline.py: never by page, 2026-10-29).
 *
 * Contents :
 *
 * * function(uniqueDiagnoses) : one per name (case, spacing and punctuation aside)
 * * function(VisitDiagnoses) : the section
 */

import { useState } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/shared/lib/utils";
import type { LinkedItem } from "@/features/patient/care-treatment/api/careTimelineTypes";
import { cardClass, greyChipClass, navyChipClass, sectionHeadingClass, textLinkClass } from "./careStyles";

const SHOWN_FIRST = 5;

const STATUS: Record<string, { label: string; className: string }> = {
  active: { label: "Active", className: "rounded-full bg-[#ECFDF3] px-2 py-0.5 text-[11px] font-semibold text-[#027A48]" },
  inactive: { label: "Inactive", className: greyChipClass },
  resolved: { label: "Resolved", className: navyChipClass },
};

const nameKey = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

// function(uniqueDiagnoses)
/** One per name, first seen first (a diagnosis read from two copies is one row). */
export function uniqueDiagnoses(items: LinkedItem[]): LinkedItem[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = nameKey(item.label);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// function(VisitDiagnoses)
export function VisitDiagnoses({ items }: { items: LinkedItem[] }) {
  const navigate = useNavigate();
  const [all, setAll] = useState(false);
  const diagnoses = uniqueDiagnoses(items);
  if (diagnoses.length === 0) return null;
  const shown = all ? diagnoses : diagnoses.slice(0, SHOWN_FIRST);

  return (
    <section aria-labelledby="visit-diagnoses" className={cardClass}>
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h2 id="visit-diagnoses" className={cn(sectionHeadingClass, "!mb-0")}>Diagnoses from this visit</h2>
        <span className="text-sm font-bold text-[#6B7280]">{diagnoses.length}</span>
      </div>
      <ul className="!mb-0 grid !pl-0" aria-label="Diagnoses from this visit">
        {shown.map((item) => {
          const status = item.status ? STATUS[item.status] : undefined;
          return (
            <li key={item.id} className="list-none border-t border-[#E5E7EB] first:border-t-0">
              <button
                type="button"
                onClick={() => navigate(`/patient/diagnosis?focus=${encodeURIComponent(item.id)}`)}
                className="flex w-full items-center gap-3 rounded-xl px-2 py-2.5 text-left hover:bg-[#FFF8F8]"
              >
                <span className="h-2 w-2 shrink-0 rounded-full bg-[#E4002B]/50" aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="text-sm font-semibold text-[#0A2947]">{item.label}</span>
                  {item.code && <span className="ml-2 text-xs text-[#9CA3AF]">{item.code}</span>}
                </span>
                {status && <span className={status.className}>{status.label}</span>}
                <Icon icon="lucide:chevron-right" className="h-4 w-4 shrink-0 text-[#9CA3AF]" aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
      {diagnoses.length > SHOWN_FIRST && (
        <button type="button" onClick={() => setAll(!all)} className={cn("mt-2 px-2", textLinkClass)}>
          {all ? "Show fewer" : `Show all ${diagnoses.length}`}
        </button>
      )}
    </section>
  );
}
